import { Helmet } from "react-helmet-async";

const SITE_ORIGIN = "https://911sourcing.com";
const DEFAULT_OG_IMAGE =
  "https://vibe.filesafe.space/1774883178162043817/assets/7c4ac38a-8a77-483d-b57a-898dd612c9ee.png";

interface SEOHeadProps {
  /** Page <title> (ideally includes brand, e.g. "Salt Supply | 911 Sourcing") */
  title: string;
  /** Meta description (keep under ~160 chars) */
  description: string;
  /** Canonical path or full URL for this page. Defaults to current pathname. */
  canonical?: string;
  /** Absolute URL for the Open Graph / Twitter share image */
  ogImage?: string;
  /** When true, adds noindex,nofollow robots tags */
  noIndex?: boolean;
  /** JSON-LD structured data object(s) to inject as a script tag */
  schemaJson?: object | object[];
}

/**
 * Centralized SEO head manager. Renders all <head> meta tags via
 * react-helmet-async so each route controls its own title, description,
 * canonical URL, Open Graph/Twitter cards, robots directives, and
 * JSON-LD structured data. Place at the top of every page, before <main>.
 */
export function SEOHead({
  title,
  description,
  canonical,
  ogImage = DEFAULT_OG_IMAGE,
  noIndex = false,
  schemaJson,
}: SEOHeadProps) {
  const canonicalUrl = canonical
    ? canonical.startsWith("http")
      ? canonical
      : `${SITE_ORIGIN}${canonical.startsWith("/") ? "" : "/"}${canonical}`
    : `${SITE_ORIGIN}${typeof window !== "undefined" ? window.location.pathname : "/"}`;

  return (
    <Helmet prioritizeSeoTags>
      {/* Title */}
      <title>{title}</title>
      <meta name="description" content={description} />

      {/* Robots */}
      {noIndex && <meta name="robots" content="noindex, nofollow" />}

      {/* Canonical */}
      <link rel="canonical" href={canonicalUrl} />

      {/* Open Graph */}
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:type" content="website" />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:site_name" content="911 Sourcing" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />

      {/* JSON-LD structured data */}
      {schemaJson && (
        <script type="application/ld+json">{JSON.stringify(schemaJson)}</script>
      )}
    </Helmet>
  );
}
